import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { reorderingReducer, initialReorderingState } from './reducer';
import { calculateDropIndex } from './utils/calculateDropIndex';
import { jsx as _jsx } from "react/jsx-runtime";
const ReorderingContext = /*#__PURE__*/createContext(null);
export const useReorderingContext = () => {
  const context = useContext(ReorderingContext);
  if (context == null) {
    throw new Error('useReorderingContext must be used within a ReorderingContext');
  }
  return context;
};
export function ReorderingContextProvider(props) {
  const {
    orientation,
    onItemOrderChange
  } = props;
  const [state, dispatch] = useReducer(reorderingReducer, Object.assign({}, initialReorderingState, {
    orientation
  }));
  const startDragging = useCallback(({
    fromIndex,
    itemSize
  }) => {
    dispatch({
      type: 'start-dragging',
      payload: {
        fromIndex,
        itemSize
      }
    });

    // Delay setting the placeholder by one frame to give React DnD time to
    // set up the drag layer.
    requestAnimationFrame(() => {
      dispatch({
        type: 'set-initial-placeholder',
        payload: {
          toIndex: fromIndex
        }
      });
    });
  }, []);
  const updateDropTarget = useCallback(({
    toIndex
  }) => {
    dispatch({
      type: 'update-drop-target',
      payload: {
        toIndex
      }
    });
  }, []);
  const updateDragState = useCallback(updates => {
    dispatch({
      type: 'update-drag-state',
      payload: updates
    });
  }, []);
  const endDragging = useCallback(() => {
    dispatch({
      type: 'end-dragging'
    });
  }, []);
  const {
    fromIndex,
    toIndex
  } = state;
  const handleDrop = useCallback(() => {
    if (fromIndex != null && toIndex != null && fromIndex !== toIndex) {
      onItemOrderChange === null || onItemOrderChange === void 0 || onItemOrderChange(fromIndex, calculateDropIndex(fromIndex, toIndex));
    }
    dispatch({
      type: 'end-dragging'
    });
  }, [fromIndex, toIndex, onItemOrderChange]);
  const reorderingContextValue = useMemo(() => Object.assign({}, state, {
    endDragging,
    handleDrop,
    startDragging,
    updateDropTarget,
    updateDragState
  }), [state, endDragging, handleDrop, startDragging, updateDropTarget, updateDragState]);
  return /*#__PURE__*/_jsx(ReorderingContext.Provider, {
    value: reorderingContextValue,
    children: props.children
  });
}